import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import moment from 'moment';
import { QueryBuilder } from '@/core/uql';
import cloneDeep from 'lodash/cloneDeep';

interface ShiftCustomPlanSemiState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    documentId: any;
    prevDocumentId: any;
    clientId: any;

    relatedPlansList: any[];
    prevPlansList: any[];

    targetForDelete: any[];
    remains: any[];
}

const state: ShiftCustomPlanSemiState = {
    loading: false,
    loaded: false,
    fatalError: false,

    documentId: null,
    prevDocumentId: null,
    clientId: null,

    relatedPlansList: [],
    prevPlansList: [],

    targetForDelete: [],
    remains: []
};

const initState: any = cloneDeep(state);

const PL_SEMI_PRODUCTS_LIST = 'PL_SEMI_PRODUCTS_LIST';
const WASHIG_SHIFT_SEMI_LIST = 'WASHIG_SHIFT_SEMI_LIST';

export const actions: ActionTree<ShiftCustomPlanSemiState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    loadData({ commit, getters, dispatch, state }, { documentId, clientId, remains, realEndTime, relTimeOfDay }) {
        const vm = (this as any)._vm;
        console.log('relTimeOfDay', relTimeOfDay)

        state.documentId = documentId;
        state.clientId = clientId;
        state.remains = remains;

        return new Promise((resolve, reject) => {
          commit('START_LOADING');
          console.debug('[ShiftCustomPlanSemi] load semi plans for shift', documentId);
          vm.$db([
            vm.$q(PL_SEMI_PRODUCTS_LIST)
              .raw('documents.list')
              .action({
                columns: {
                  id: 'scp.id',
                  full_id: 'sfpc.id',
                  number: 'sfpc.number',
                  product_name: 'p.name',
                  line_name: 'vl.name',
                },
                from: 's:shift_full_plan_custom',
                from_as: 'sfpc',
                joins: [
                  {
                    join: 's:shift_custom_plan',
                    type: 'left',
                    as:   'scp',
                    on: {
                      $eq: ['sfpc.plan_id', 'scp.id']
                    }
                  },
                  {
                    join: 's:products',
                    type: 'left',
                    as:   'p',
                    on: {
                      $eq: ['scp.product_id', 'p.id']
                    }
                  },
                  {
                    join: 's:voc_lines',
                    type: 'left',
                    as:   'vl',
                    on: {
                      $eq: ['scp.line_id', 'vl.id']
                    }
                  },
                ],
                order: [
                ],
                page_size: 100,
                where: {
                  $and: [
                    {$eq: [`scp.shift_id`, {$: documentId}]},
                    {$eq: [`scp.kind`, {$: 'semi'}]},
                  ]
                }
              }),
            vm.$q(WASHIG_SHIFT_SEMI_LIST)
              .raw('documents.list')
              .action({
                columns: {
                  id: 'sfpc.id',
                  full_id: 'sfpc.id',
                  number: 'sfpc.number',
                  base_name: 'vv.name',
                  line_name: 'vl.name',
                  wash_type: 'vwt.name',
                  water_volume: 'vvw.water_volume',
                  detergent_volume: 'vvw.detergent_volume',
                  detergent_concentration: 'vvw.detergent_concentration',
                  detergent_name: 'vd.name',
                },
                from: 's:shift_full_plan_custom',
                from_as: 'sfpc',
                joins: [
                  {
                    join: 's:shift_plan_washing',
                    as: 'spw',
                    on: {
                      $eq: ['spw.id', 'sfpc.wash_id']
                    }
                  },
                  {
                    join: 's:voc_volumes',
                    as: 'vv',
                    on: {
                      $eq: ['vv.id', 'spw.equipment_id']
                    }
                  },
                  {
                    join: 's:voc_volumes_washings',
                    as: 'vvw',
                    on: {
                      $eq: ['vvw.id', 'spw.washing_id']
                    }
                  },
                  {
                    join: 's:washings',
                    as: 'w',
                    on: {
                      $eq: ['w.id', 'vvw.washing_id']
                    }
                  },
                  {
                    join: 's:voc_washing_types',
                    as: 'vwt',
                    on: {
                      $eq: ['vwt.id', 'w.type_id']
                    }
                  },
                  {
                    join: 's:voc_detergents',
                    as: 'vd',
                    on: {
                      $eq: ['vd.id', 'w.detergent_id']
                    }
                  },
                  {
                    join: 's:voc_lines',
                    type: 'left',
                    as:   'vl',
                    on: {
                      $eq: ['vl.templates', {$: 'wash'}]
                    }
                  },
                ],
                where: {
                  $eq: [`sfpc.shift_id`, {$: documentId}]
                }
              })
          ]).then((result: ServerResult) => {
            const opRelResult = result.getOperation(PL_SEMI_PRODUCTS_LIST);
            const opWashResult = result.getOperation(WASHIG_SHIFT_SEMI_LIST);
            if (!opRelResult.hasError()) {
              state.relatedPlansList = opRelResult.getContent().documents.map((doc: any) => {
                return doc
              })
              state.relatedPlansList.push(...opWashResult.getContent().documents.map((doc: any) => {
                doc.type = 'wash'
                return doc
              }))
              console.debug('[ShiftCustomPlanSemi] loaded plans',
                'semi products:', opRelResult.getContent().documents.length,
                'washes:', opWashResult.getContent().documents.length);
            }
            commit('LOADED_SUCCESS');

            resolve(null);
          });
        })
    },

    deleteDocument({ state, dispatch }, row) {
        const vm = (this as any)._vm;

        vm.$db([
            (vm.$q('DELETE_DOCUMENT') as QueryBuilder)
              .update(row.id, { deleted_at: moment().utc().local().format() })
        ])
        .then(() => {
            dispatch('loadData', { documentId: state.documentId, silence: true})
        })
    },

    restoreDocument({ state, dispatch }, row) {
        const vm = (this as any)._vm;

        vm.$db([
            (vm.$q('DELETE_DOCUMENT') as QueryBuilder)
              .update(row.id, { deleted_at: null })
        ])
        .then(() => {
            dispatch('loadData', { documentId: state.documentId, silence: true})
        })
    },
};

export const mutations: MutationTree<ShiftCustomPlanSemiState> = {
  RESET_STATE: (state) => {
      for(const key in initState) {
          (state as any)[key] = initState[key]
      }
  },
  START_LOADING: (state) => {
      state.loaded = false;
      state.loading = true;
      state.fatalError = false;
  },
  LOADED_SUCCESS: (state) => {
      state.loaded = true;
      state.loading = false;
      state.fatalError = false;
  },
  LOADED_ERROR: (state) => {
      state.loaded = true;
      state.loading = false;
      state.fatalError = true;
  },
};

export const STORE_KEY = '$_shift_custom_plan_semi_list';

export const store = {
    namespaced: true,
    state,
    actions,
    mutations,
};
